import { useState, useEffect } from 'react';
import { Settings, Save, Loader2, AlertCircle, CheckCircle2, Lock } from 'lucide-react';
import { useSettings, useUpdateSetting } from '../hooks/useSettings';
import type { Setting } from '../services/settingsApi';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';
import { AssistantSpendSettings } from '../components/settings/AssistantSpendSettings';

const CATEGORY_META: Record<string, { label: string; description: string }> = {
  platform: { label: 'Platform Info', description: 'General platform configuration and identification.' },
  notifications: { label: 'Notification Preferences', description: 'Configure how and when notifications are delivered.' },
  appearance: { label: 'Appearance', description: 'Customize the visual presentation of the dashboard.' },
  integration: { label: 'Integration', description: 'External service connections and API configuration.' },
};

const CATEGORY_ORDER = ['platform', 'notifications', 'appearance', 'integration'];

export function AdminSettings() {
  const { data: settings, isLoading, isError } = useSettings();
  const updateMutation = useUpdateSetting();
  const [editingValues, setEditingValues] = useState<Record<string, string>>({});
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const handleChange = (category: string, key: string, value: string) => {
    setEditingValues((prev) => ({ ...prev, [`${category}:${key}`]: value }));
  };

  const getValue = (category: string, setting: Setting): string => {
    const editKey = `${category}:${setting.key}`;
    return editKey in editingValues ? editingValues[editKey] : setting.value;
  };

  const isDirty = (category: string, setting: Setting): boolean => {
    const editKey = `${category}:${setting.key}`;
    return editKey in editingValues && editingValues[editKey] !== setting.value;
  };

  const handleSave = async (category: string, setting: Setting) => {
    const value = getValue(category, setting);
    try {
      await updateMutation.mutateAsync({ category, key: setting.key, value });
      const editKey = `${category}:${setting.key}`;
      setEditingValues((prev) => {
        const next = { ...prev };
        delete next[editKey];
        return next;
      });
      setSavedKey(editKey);
      setTimeout(() => setSavedKey(null), 2000);
    } catch {
      // Error handled by mutation state
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 animate-spin text-ems-green" />
      </div>
    );
  }

  if (isError || !settings) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertCircle className="w-8 h-8 text-ems-red mx-auto mb-2" />
          <p className="text-sm text-ems-text-muted">Failed to load settings.</p>
        </div>
      </div>
    );
  }

  const categories = CATEGORY_ORDER.filter((cat) => settings[cat]);

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex items-center gap-3">
        <Settings className="w-5 h-5 text-ems-green" />
        <div>
          <h1 className="text-lg font-bold text-ems-text-primary">Platform Settings</h1>
          <p className="text-sm text-ems-text-muted">Manage platform configuration and preferences.</p>
        </div>
      </div>

      {categories.map((category) => {
        const meta = CATEGORY_META[category] || { label: category, description: '' };
        const items = settings[category];

        return (
          <div key={category} className="bg-ems-bg-card rounded-2xl border border-ems-border">
            <div className="px-6 py-4 border-b border-ems-border">
              <h2 className="text-sm font-semibold text-ems-text-primary">{meta.label}</h2>
              <p className="text-xs text-ems-text-muted mt-0.5">{meta.description}</p>
            </div>
            <div className="divide-y divide-ems-border">
              {items.map((setting) => (
                <SettingRow
                  key={setting.key}
                  category={category}
                  setting={setting}
                  value={getValue(category, setting)}
                  dirty={isDirty(category, setting)}
                  saving={updateMutation.isPending}
                  saved={savedKey === `${category}:${setting.key}`}
                  onChange={(v) => handleChange(category, setting.key, v)}
                  onSave={() => handleSave(category, setting)}
                />
              ))}
            </div>
          </div>
        );
      })}

      {/* B.10: Lockout Policy Settings */}
      <LockoutPolicySection />

      {/* #861: per-tenant AI assistant spend. Renders only for platform_admin —
          this page is open to customer_admin, and spend is cross-tenant data. */}
      <AssistantSpendSettings />
    </div>
  );
}

interface SettingRowProps {
  category: string;
  setting: Setting;
  value: string;
  dirty: boolean;
  saving: boolean;
  saved: boolean;
  onChange: (value: string) => void;
  onSave: () => void;
}

interface LockoutPolicy {
  rule_id: string | null;
  threshold: number;
  window_minutes: number;
  auto_unlock_minutes: number;
}

function LockoutPolicySection() {
  const queryClient = useQueryClient();
  const { data: policy, isLoading } = useQuery<LockoutPolicy>({
    queryKey: ['security', 'lockoutPolicy'],
    queryFn: async () => {
      const res = await api.get<LockoutPolicy>('/v1/security/lockout-policy');
      return res.data;
    },
  });

  const [editState, setEditState] = useState<Partial<LockoutPolicy>>({});
  const [policyDirty, setPolicyDirty] = useState(false);
  const [policySaved, setPolicySaved] = useState(false);

  useEffect(() => {
    if (policy) {
      setEditState({
        threshold: policy.threshold,
        window_minutes: policy.window_minutes,
        auto_unlock_minutes: policy.auto_unlock_minutes,
      });
    }
  }, [policy]);

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<LockoutPolicy>) => {
      const res = await api.put('/v1/security/lockout-policy', data);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['security', 'lockoutPolicy'] });
      setPolicyDirty(false);
      setPolicySaved(true);
      setTimeout(() => setPolicySaved(false), 2000);
    },
  });

  const handlePolicyChange = (field: string, value: string) => {
    const numVal = parseInt(value, 10);
    if (isNaN(numVal)) return;
    setEditState(prev => ({ ...prev, [field]: numVal }));
    setPolicyDirty(true);
  };

  const handlePolicySave = () => {
    saveMutation.mutate(editState);
  };

  if (isLoading) {
    return (
      <div className="bg-ems-bg-card rounded-2xl border border-ems-border p-6">
        <Loader2 className="w-5 h-5 animate-spin text-ems-green mx-auto" />
      </div>
    );
  }

  return (
    <div className="bg-ems-bg-card rounded-2xl border border-ems-border">
      <div className="px-6 py-4 border-b border-ems-border">
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-ems-green" />
          <h2 className="text-sm font-semibold text-ems-text-primary">Account Lockout Policy</h2>
        </div>
        <p className="text-xs text-ems-text-muted mt-0.5">
          Configure the automatic account lockout after failed login attempts.
        </p>
      </div>
      <div className="divide-y divide-ems-border">
        <div className="px-6 py-4 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ems-text-primary">Failed Attempt Threshold</p>
            <p className="text-xs text-ems-text-muted mt-0.5">Number of failed login attempts before the account is locked.</p>
          </div>
          <input
            type="number"
            min={1}
            max={100}
            value={editState.threshold ?? 5}
            onChange={(e) => handlePolicyChange('threshold', e.target.value)}
            className="text-sm border border-ems-border rounded-lg px-3 py-1.5 w-24 text-ems-text-primary bg-ems-bg focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
          />
        </div>
        <div className="px-6 py-4 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ems-text-primary">Monitoring Window (minutes)</p>
            <p className="text-xs text-ems-text-muted mt-0.5">Time window in which failed attempts are counted.</p>
          </div>
          <input
            type="number"
            min={1}
            max={1440}
            value={editState.window_minutes ?? 15}
            onChange={(e) => handlePolicyChange('window_minutes', e.target.value)}
            className="text-sm border border-ems-border rounded-lg px-3 py-1.5 w-24 text-ems-text-primary bg-ems-bg focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
          />
        </div>
        <div className="px-6 py-4 flex items-center gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-ems-text-primary">Auto-Unlock Timer (minutes)</p>
            <p className="text-xs text-ems-text-muted mt-0.5">Minutes after which a locked account is automatically unlocked.</p>
          </div>
          <input
            type="number"
            min={1}
            max={10080}
            value={editState.auto_unlock_minutes ?? 30}
            onChange={(e) => handlePolicyChange('auto_unlock_minutes', e.target.value)}
            className="text-sm border border-ems-border rounded-lg px-3 py-1.5 w-24 text-ems-text-primary bg-ems-bg focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
          />
        </div>
      </div>
      {/* Save bar */}
      <div className="px-6 py-3 border-t border-ems-border flex items-center justify-end gap-2">
        {policySaved && <CheckCircle2 className="w-4 h-4 text-ems-green" />}
        <button
          onClick={handlePolicySave}
          disabled={!policyDirty || saveMutation.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-ems-forest text-ems-green border border-ems-green/20 text-xs font-medium hover:bg-ems-forest/80 disabled:opacity-50 transition-colors"
        >
          {saveMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save Policy
        </button>
      </div>
    </div>
  );
}

function SettingRow({ setting, value, dirty, saving, saved, onChange, onSave }: SettingRowProps) {
  return (
    <div className="px-6 py-4 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-ems-text-primary">{setting.label}</p>
        <p className="text-xs text-ems-text-muted mt-0.5">{setting.description}</p>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {setting.type === 'boolean' ? (
          <button
            onClick={() => onChange(value === 'true' ? 'false' : 'true')}
            className={`relative w-10 h-5 rounded-full transition-colors ${
              value === 'true' ? 'bg-ems-green' : 'bg-ems-text-muted'
            }`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-4 h-4 bg-ems-bg-card rounded-full transition-transform ${
                value === 'true' ? 'translate-x-5' : ''
              }`}
            />
          </button>
        ) : setting.type === 'select' && setting.options ? (
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="text-sm border border-ems-border rounded-lg px-3 py-1.5 text-ems-text-primary bg-ems-bg focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
          >
            {setting.options.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        ) : (
          <input
            type={setting.type === 'number' ? 'number' : 'text'}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="text-sm border border-ems-border rounded-lg px-3 py-1.5 w-48 text-ems-text-primary bg-ems-bg focus:outline-none focus:ring-2 focus:ring-ems-green/20 focus:border-ems-green"
          />
        )}
        {dirty && (
          <button
            onClick={onSave}
            disabled={saving}
            className="p-1.5 rounded-lg bg-ems-forest text-ems-green hover:bg-ems-forest/80 disabled:opacity-50 transition-colors"
            title="Save"
          >
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          </button>
        )}
        {saved && <CheckCircle2 className="w-4 h-4 text-ems-green" />}
      </div>
    </div>
  );
}
